import React, { useEffect, useState } from "react";
import { Table, Card, Typography, Tag, Input, Button, Row, Col, Select, DatePicker, Avatar, Space, Tooltip } from "antd";
import { SearchOutlined, DownloadOutlined, FilterOutlined, UserOutlined, GlobalOutlined } from "@ant-design/icons";
import axiosInstance from "../../configs";
import dayjs from "dayjs";
import { getUserLevel, USER_LEVELS } from "../../utils/rbac";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Level yang bisa melihat semua log
const LEVELS_CAN_VIEW_ALL = [USER_LEVELS.SUPER_ADMIN, "2"]; // Super Admin (1) dan Bea Cukai (2)

export default function UserActivityLog() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [filters, setFilters] = useState({
        module: "All Modules",
        action: "All Actions",
        dateRange: null
    });

    // Get current user info
    const getCurrentUser = () => {
        try {
            const userStr = localStorage.getItem("user");
            if (!userStr) return null;
            return JSON.parse(userStr);
        } catch (e) {
            return null;
        }
    };

    const fetchData = async (search = "") => {
        setLoading(true);
        try {
            const currentUser = getCurrentUser();
            const userLevel = getUserLevel();

            // Buat params untuk API call
            const params = { limit: 100, search };

            // Jika bukan Super Admin atau Bea Cukai, filter berdasarkan user sendiri
            if (!LEVELS_CAN_VIEW_ALL.includes(userLevel) && currentUser) {
                params.id_user = currentUser.id_user;
            }

            // Add date range filter if set
            if (filters.dateRange && filters.dateRange.length === 2) {
                params.start_date = filters.dateRange[0].format("YYYY-MM-DD");
                params.end_date = filters.dateRange[1].format("YYYY-MM-DD");
            }

            const res = await axiosInstance.get("/logs", { params });

            // Handle both array response and object with data property
            const fetchedData = res.data?.data || res.data || [];

            setData(fetchedData);
        } catch (e) {
            console.error("Failed to load logs", e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleSearch = (e) => {
        const val = e.target.value;
        setSearchText(val);
        if (val.length === 0) fetchData();
    };

    const onSearchSubmit = () => fetchData(searchText);

    const openMap = (lat, long) => {
        if (!lat || !long) return;
        window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${long}`, "_blank");
    };

    const columns = [
        {
            title: "TIMESTAMP",
            key: "timestamp",
            width: 140,
            render: (_, r) => (
                <div style={{ lineHeight: "1.2" }}>
                    <Text strong style={{ display: "block" }}>{dayjs(r.tgl).format("YYYY-MM-DD")}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{r.waktu} WIB</Text>
                </div>
            ),
        },
        {
            title: "ACTOR",
            key: "actor",
            width: 200,
            render: (_, r) => (
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#fde3cf', color: '#f56a00' }} />
                    <div style={{ lineHeight: "1.2" }}>
                        <Text strong style={{ display: "block" }}>{r.user_name || "System"}</Text>
                        <Text type="secondary" style={{ fontSize: 11 }}>{r.username || "Unknown Role"}</Text>
                    </div>
                </div>
            ),
        },
        {
            title: "ACTION",
            dataIndex: "activity",
            key: "activity",
            width: 100,
            render: (t) => {
                let color = "default";
                let text = t ? t.toUpperCase() : "UNKNOWN";
                if (text === "LOGIN") color = "processing"; // Blueish
                if (text === "UPDATE" || text === "PUT") color = "geekblue";
                if (text === "CREATE" || text === "POST" || text === "INSERT") color = "success";
                if (text === "DELETE") color = "error";
                return <Tag color={color} style={{ fontWeight: 600 }}>{text}</Tag>;
            },
        },
        {
            title: "MODULE",
            dataIndex: "module",
            key: "module",
            width: 150,
            render: (t) => <Text>{t || "General"}</Text>,
        },
        {
            title: "RECORD ID",
            key: "record_id",
            width: 120,
            render: (_, r) => (
                <Text style={{ color: '#1890ff', cursor: 'pointer', fontWeight: 500 }}>
                    {r.record_id || `LOG-${r.id_user_log}`}
                </Text>
            ),
        },
        {
            title: "LONGLAT",
            key: "longlat",
            width: 140,
            render: (_, r) => {
                const lat = r.latitude;
                const long = r.longitude;

                if (!lat || !long) return <Text disabled>-</Text>;

                return (
                    <div
                        onClick={() => openMap(lat, long)}
                        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#1890ff' }}
                    >
                        <GlobalOutlined />
                        <Text style={{ color: 'inherit', fontSize: 12 }}>{lat}, {long}</Text>
                    </div>
                );
            }
        },
        {
            title: "IP ADDRESS",
            dataIndex: "ip_address",
            key: "ip_address",
            width: 120,
            render: (t) => <Text type="secondary">{t || "127.0.0.1"}</Text>,
        },
    ];

    const expandedRowRender = (record) => {
        const newValue = tryParse(record.new_value);
        // Mocking previous value for demo purposes as it might not be in the current API
        const prevValue = record.old_value ? tryParse(record.old_value) : { Info: "No previous data recorded" };

        return (
            <Card type="inner" title="CHANGE DETAILS" size="small" style={{ margin: 0, background: '#fafafa' }}>
                <Row gutter={[16, 12]}>
                    <Col xs={24} md={12}>
                        <div style={{
                            background: '#fff1f0',
                            padding: 10,
                            borderRadius: 6,
                            border: '1px solid #ffa39e',
                            height: '100%'
                        }}>
                            <Text type="danger" strong style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                                PREVIOUS VALUE
                            </Text>
                            <pre style={{
                                margin: 0,
                                fontSize: 10,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                maxHeight: 150,
                                overflowY: 'auto'
                            }}>
                                {JSON.stringify(prevValue, null, 2)}
                            </pre>
                        </div>
                    </Col>
                    <Col xs={24} md={12}>
                        <div style={{
                            background: '#f6ffed',
                            padding: 10,
                            borderRadius: 6,
                            border: '1px solid #b7eb8f',
                            height: '100%'
                        }}>
                            <Text type="success" strong style={{ fontSize: 11, display: 'block', marginBottom: 6 }}>
                                NEW VALUE
                            </Text>
                            <pre style={{
                                margin: 0,
                                fontSize: 10,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                maxHeight: 150,
                                overflowY: 'auto'
                            }}>
                                {JSON.stringify(newValue, null, 2)}
                            </pre>
                        </div>
                    </Col>
                </Row>
            </Card>
        );
    };

    const handleExport = async () => {
        try {
            const res = await axiosInstance.get("/logs/export", {
                params: { search: searchText },
                responseType: "blob", // Important for binary data
            });

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Audit_Log_${dayjs().format("YYYY-MM-DD_HH-mm")}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (e) {
            console.error("Export failed", e);
        }
    };

    return (
        <div style={{ padding: '16px' }}>
            {/* Header Section - Responsive */}
            <div style={{ marginBottom: 20 }}>
                <Row gutter={[16, 16]} justify="space-between" align="middle">
                    <Col xs={24} md={14}>
                        <Title level={3} style={{ margin: 0, fontWeight: 700 }}>
                            Audit Log & Traceability
                        </Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            Immutable record of system events for Customs Compliance.
                        </Text>
                    </Col>
                    <Col xs={24} md={10} style={{ textAlign: 'right' }}>
                        <Space wrap size="small">
                            <Button
                                icon={<FilterOutlined />}
                                size="middle"
                            >
                                <span className="hide-on-mobile">Advanced </span>Filters
                            </Button>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={handleExport}
                                size="middle"
                            >
                                Export<span className="hide-on-mobile"> Log</span>
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </div>

            {/* Filters Bar - Responsive */}
            <Card
                bodyStyle={{ padding: '12px 16px' }}
                style={{ marginBottom: 20, borderRadius: 8 }}
            >
                <Row gutter={[12, 12]} align="middle">
                    {/* Search Input - Full width on mobile */}
                    <Col xs={24} sm={24} md={8} lg={10}>
                        <Input
                            placeholder="Search Record ID, User, or Keyword"
                            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                            value={searchText}
                            onChange={handleSearch}
                            onPressEnter={onSearchSubmit}
                            size="middle"
                            style={{ width: '100%' }}
                        />
                    </Col>

                    {/* Date Range - Full width on mobile, half on tablet */}
                    <Col xs={24} sm={12} md={6} lg={6}>
                        <RangePicker
                            size="middle"
                            style={{ width: '100%' }}
                            placeholder={['Start', 'End']}
                        />
                    </Col>

                    {/* Module Select */}
                    <Col xs={12} sm={6} md={5} lg={4}>
                        <Select
                            defaultValue="All Modules"
                            size="middle"
                            style={{ width: '100%' }}
                        >
                            <Option value="All Modules">All Modules</Option>
                            <Option value="Inventory">Inventory</Option>
                            <Option value="Customs">Customs</Option>
                            <Option value="Auth">Auth</Option>
                        </Select>
                    </Col>

                    {/* Action Select */}
                    <Col xs={12} sm={6} md={5} lg={4}>
                        <Select
                            defaultValue="All Actions"
                            size="middle"
                            style={{ width: '100%' }}
                        >
                            <Option value="All Actions">All Actions</Option>
                            <Option value="CREATE">Create</Option>
                            <Option value="UPDATE">Update</Option>
                            <Option value="DELETE">Delete</Option>
                            <Option value="LOGIN">Login</Option>
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* Data Table - Responsive with horizontal scroll */}
            <Card bodyStyle={{ padding: 0 }} style={{ borderRadius: 8, overflow: 'hidden' }}>
                <Table
                    dataSource={data}
                    columns={columns}
                    rowKey="id_user_log"
                    expandable={{
                        expandedRowRender,
                        expandIconColumnIndex: 0,
                    }}
                    loading={loading}
                    pagination={{
                        pageSize: 10,
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        size: 'small',
                        showSizeChanger: false,
                    }}
                    scroll={{ x: 900 }}
                    size="small"
                />
            </Card>

            {/* CSS for mobile responsiveness */}
            <style>{`
                @media (max-width: 576px) {
                    .hide-on-mobile {
                        display: none;
                    }
                    .ant-table-thead > tr > th {
                        font-size: 11px;
                        padding: 8px 4px !important;
                    }
                    .ant-table-tbody > tr > td {
                        font-size: 12px;
                        padding: 8px 4px !important;
                    }
                    .ant-pagination {
                        font-size: 12px;
                    }
                    .ant-picker {
                        font-size: 12px;
                    }
                    .ant-select {
                        font-size: 12px;
                    }
                }
                @media (max-width: 768px) {
                    .ant-table-expanded-row .ant-card {
                        margin: 0 !important;
                    }
                    .ant-table-expanded-row pre {
                        font-size: 10px !important;
                        max-height: 150px;
                        overflow-y: auto;
                    }
                }
            `}</style>
        </div>
    );
}

function tryParse(str) {
    if (!str) return null;
    try {
        return JSON.parse(str);
    } catch (e) {
        return str;
    }
}
